import styled from '@emotion/styled'
import Button from '@/components/Button'
import {
  Container as SelectContainer,
  Label as SelectLabel,
  Wrapper as SelectLabelWrapper,
  DropDownWrapper as SelectDropDownWrapper,
  DropDownList as SelectDropDownList,
  DropDownItem as SelectDropDownItem,
  DropDownItemLabel as SelectDropDownItemLabel
} from '@/components/Forms/Inputs/Select/style'

import { mediaMax, styledScrollbar } from '@/styles/mixins'

export const Container = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.lightVanilla3};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 60px 60px;
  padding-bottom: 30px;

  ${mediaMax.md} {
    padding: 60px ${p => p.theme.paddingX.t};
  }

  ${mediaMax.xs} {
    padding: 3rem 0;
  }
`

export const CardHead = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;

  ${mediaMax.xs} {
    padding: 0 ${p => p.theme.paddingX.m};;
  }
`

export const Name = styled.h1`
  font-family: ${({ theme }) => theme.fonts.serif};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.greyText};
  font-size: 3.5rem;
  line-height: 4.5rem;

  ${mediaMax.xs} {
    font-size: 3.6rem;
  }
`

export const SelectWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;

  ${SelectContainer} {
    background-color: transparent;
    border-radius: 0;
  }

  ${SelectLabel} {
    font-family: ${({ theme }) => theme.fonts.sansSerif};
    color: ${({ theme }) => theme.colors.greyText};
    font-size: 2rem;
    line-height: 3.5rem;
    text-transform: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.black};

    ${mediaMax.xs} {
      font-size: 2.4rem;
    }
  }

  ${SelectLabelWrapper} {
    padding: 0;
    border: none;
    max-width: 165px;
    display: flex;
    align-self: flex-start;

    ${mediaMax.xs}{
      max-width: 195px;
    }
  }

  ${SelectDropDownWrapper} {
    overflow-x: hidden;
    border: 2px solid ${({ theme }) => theme.colors.brown};
    background-color: ${({ theme }) => theme.colors.white};
    padding: 12px 12px 12px 0;
  }

  ${SelectDropDownList} {
    align-items: flex-start;
    justify-content: flex-start;
    padding: 0;
    height: 100%;
    overflow: hidden auto;

    &::-webkit-scrollbar {
      width: 3px;
    }

    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.colors.lightGrey};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${({ theme }) => theme.colors.lightGreen2};
      border-radius: 0;
      border: none;
    }
  }

  ${SelectDropDownItemLabel} {
    font-family: ${({ theme }) => theme.fonts.sansSerif};
    color: ${({ theme }) => theme.colors.greyText};
    font-size: 1.4rem;
    line-height: normal;
    text-transform: none;
    transition: color ease-out 0.3s;
  }

  ${SelectDropDownItem} {
    cursor: pointer;
    justify-content: flex-start;
    padding: 5px 24px;
    background-color: transparent;

    &.active {
      ${SelectDropDownItemLabel} {
        color: ${({ theme }) => theme.colors.lightGreen2};
      }
    }

    &:hover {
      background-color: transparent;

      ${SelectDropDownItemLabel} {
        color: ${({ theme }) => theme.colors.lightGreen2};
      }
    }
  }
`

export const Price = styled.span`
  font-family: ${({ theme }) => theme.fonts.sansSerif};
  color: ${({ theme }) => theme.colors.greyText};
  font-size: 1.5rem;
  line-height: 1.8rem;
  margin: 2rem 0 0;

  ${mediaMax.xs} {
    margin-top: 1.6rem;
    font-size: 2.4rem;
  }
`

export const CardContent = styled.div`
  margin-top: 2rem;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;

  ${mediaMax.xs} {
    padding: 0 5vw;
  }
`

export const CardContentTop = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

export const CardNav = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

export const CardNavItem = styled.span`
  font-family: ${({ theme }) => theme.fonts.sansSerif};
  color: ${({ theme }) => theme.colors.grey};
  font-size: 1.1rem;
  letter-spacing: .1rem;
  margin-right: 25px;
  text-transform: uppercase;
  position: relative;
  transition: all ease-out 0.3s;
  cursor: pointer;
  ${mediaMax.xs}{
    margin-right: 20px;
  }

  &:after {
    content: '';
    display: flex;
    width: 100%;
    height: 1px;
    position: absolute;
    left: 0;
    top: calc(100% + 5px);
    opacity: 0;
    background-color: ${({ theme }) => theme.colors.greyText};
    transition: all ease-out 0.3s;
  }

  &.active {
    color: ${({ theme }) => theme.colors.greyText};

    &:after {
      opacity: 1;
    }
  }

  &:last-child {
    margin-right: 0;
  }
`

export const CardContentWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;

  position: relative;
  overflow: hidden;
  height: ${({ tabHeight }) => (tabHeight ? `${tabHeight}px` : 'auto')};
  transition: height ease-out 0.3s 0.3s;
`

export const CardContentTab = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  left: 100%;
  transition: opacity ease-out 0.3s, left ease-out 0.3s 0.6s;
  padding: 1rem 0 3rem;

  &.active {
    position: relative;
    left: 0;
    opacity: 1;
    pointer-events: initial;
    transition: opacity ease-out 0.3s 0.6s;
  }
`

export const CardContentItem = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const CardContentItemDesc = styled(CardContentItem)`
  //align-items: flex-start;
  display: initial;
  font-family: ${({ theme }) => theme.fonts.sansSerif};
  color: ${({ theme }) => theme.colors.grey};
  font-size: 1.2rem;
  letter-spacing: .05rem;
  line-height: 2rem;
  max-height: 35vh;
  overflow-y: scroll;
  padding-right: 1.5rem;

  b, strong {
    font-weight: bold;
  }

  p > b, p > strong {
    color: ${({ theme }) => theme.colors.greyText};
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.paragraph.lg};
    font-family: ${p => p.theme.fonts.sansSerif};
    letter-spacing: .1rem;
    line-height: 1.8rem;
    // padding: .5rem 0;
    text-align: left;

    ${mediaMax.xs} {
      font-size: ${({ theme }) => theme.fontSizes.paragraph.xs};
    }
  }

  ul {
    margin-top: 2rem;
    font-family: ${({ theme }) => theme.fonts.sansSerif};
    color: ${p => p.theme.colors.grey};
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    list-style: inside;

    li {
      line-height: 1.8rem;
    }
  }

  table{
    color: ${p => p.theme.colors.grey};
    tr{

      td{
        padding: 1rem 0;
        border-bottom: 1px solid ${p => p.theme.colors.grey}30;
      }

      td:first-of-type{
        border-right: 1px solid ${p => p.theme.colors.grey}30;
        padding-right: 1rem;
      }

      td:last-child{
        padding-left: 1rem;
      }

    }
  }

  h2, p.title-legals {
    color: ${p => p.theme.colors.black};
    font-weight: 900;
    font-size: 24px;
    margin-top: 50px;
    margin-bottom: 20px;
  }
  h3 {
    font-family: ${({ theme }) => theme.fonts.serif};
    color: ${p => p.theme.colors.black};
    font-size: 24px;
    margin-bottom: 20px;
    margin-top: 10px;
    line-height: 3rem;
  }

  a, a:visited {
    color: ${p => p.theme.colors.grey};
  }

  &::-webkit-scrollbar {
    display: none;
  }
`

export const CardContentItemDetails = styled(CardContentItem)`
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: stretch;
`

export const DetailsItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: calc(50% - .4rem);
  margin-bottom: .8rem;
  padding: 2.2rem 3rem;
  background-color: ${({ theme }) => theme.colors.lightGrey4};
  white-space: pre-wrap;
  word-break: break-word;


  ${mediaMax.xs} {
    width: 100%;
  }
`

export const DetailsItemPicto = styled.div`
  //width: 76px;
`

export const DetailsItemContent = styled.div`
  padding-left: 25px;
`

export const DetailsItemContentLabel = styled.div`
  font-family: ${({ theme }) => theme.fonts.sansSerif};
  color: ${({ theme }) => theme.colors.grey};
  font-size: 1.2rem;
  margin-bottom: .8rem;
  white-space: pre-wrap;
`

export const DetailsItemContentInfo = styled.div`
  font-family: ${({ theme }) => theme.fonts.sansSerif};
  color: ${({ theme }) => theme.colors.grey};
  font-size: 1.4rem;
  white-space: pre-wrap;
  word-break: break-word;
`

export const CardFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
`

export const FindStoreButton = styled(Button)`
    ${mediaMax.xs} {
      width: 50%;
      right: 0;
      z-index: 97;
      position: fixed;
      bottom: 0;
      border-left: 1px solid ${({ theme }) => theme.colors.white};
    }
`

export const BuyOnlineButton = styled(Button)`
  display: none;

  ${mediaMax.xs} {
    width: 50%;
    display: initial;
    z-index: 97;
    position: fixed;
    bottom: 0;
    left: 0;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
  }
`

export const FindPointOfSaleButton = styled(Button)`
  ${mediaMax.xs} {
    z-index: 97;
    position: fixed;
    bottom: 0;
  }
`

export const BuyButton = styled(Button)`
  ${mediaMax.xs} {
    z-index: 97;
    position: fixed;
    bottom: 0;
  }
`

export const Top = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

export const StoresList = styled.div`
  display: flex;
  flex-direction: column;
  height: 150px;
  min-height: 150px;
  width: 100%;
  margin-bottom: 2.2rem;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  ${styledScrollbar()}
  ${mediaMax.xs}{
    height: 150px;
  }
`
